import { useState, useEffect, useRef, useCallback } from 'react'

import { useWidgetStore } from '@/stores/useWidgetStore'
import type { DisplaySettings, DisplaySettingsTriggerType, DynamicDisplayTrigger } from '@/types'
import { checkUrlTargets } from '@/utils/checkUrlTargets'
import { shouldShowByFrequency, recordDisplay, getFrequencyRemainingMs } from '@/utils/displayFrequency'

/** Treat null/undefined enabled as true (enabled by default); only false disables. */
function isTriggerEnabled(trigger?: DynamicDisplayTrigger): trigger is DynamicDisplayTrigger {
  if (!trigger) return false
  if (trigger.enabled === false) return false
  return true
}

/**
 * Hook to determine if a component should be displayed based on display settings.
 *
 * Evaluates URL targeting, display frequency, and trigger conditions (onLoad, onScroll, onClick, onExit).
 * Demo/preview skips those live gates; component-level `enabled` flags still apply.
 *
 * @param displaySettings - The display settings configuration
 * @param storageKey - Unique key for localStorage frequency tracking (e.g., 'widget-icon')
 * @param settingsReady - Whether the parent settings have loaded. When false, the hook
 *   returns false to prevent showing before settings are available. Defaults to true for
 *   backward compatibility.
 * @returns boolean indicating whether the component should be shown
 */
export function useDisplayTrigger(
  displaySettings?: DisplaySettings,
  storageKey?: string,
  settingsReady: boolean = true
): boolean {
  const demo = useWidgetStore((state) => state.demo)
  const [shouldShow, setShouldShow] = useState(false)
  const [frequencyRetry, setFrequencyRetry] = useState(0)
  const hasRecordedDisplay = useRef(false)
  const clickCountRef = useRef(0)

  // Record display when component becomes visible (only once per mount)
  const recordDisplayOnce = useCallback(() => {
    if (storageKey && !hasRecordedDisplay.current) {
      recordDisplay(storageKey)
      hasRecordedDisplay.current = true
    }
  }, [storageKey])

  useEffect(() => {
    // Reset state on settings change (frequencyRetry triggers re-evaluation after frequency window expires)
    void frequencyRetry
    setShouldShow(false)
    hasRecordedDisplay.current = false
    clickCountRef.current = 0

    // Don't evaluate until parent settings have loaded
    if (!settingsReady) {
      return
    }

    // widget.aiq.com does not match merchant include URLs; skip live gates in preview
    if (demo) {
      setShouldShow(true)
      return
    }

    // If no display settings, always show
    if (!displaySettings) {
      setShouldShow(true)
      return
    }

    // Check URL targets first
    if (displaySettings.targets) {
      if (!checkUrlTargets(window.location.href, displaySettings.targets)) {
        return // URL doesn't match, don't show
      }
    }

    // Check display frequency — if blocked, schedule a re-check when the window expires
    if (storageKey && !shouldShowByFrequency(storageKey, displaySettings.displayFrequency)) {
      const remainingMs = getFrequencyRemainingMs(storageKey, displaySettings.displayFrequency)
      if (remainingMs > 0) {
        const timer = setTimeout(() => setFrequencyRetry((c) => c + 1), remainingMs + 50)
        return () => clearTimeout(timer)
      }
      return
    }

    const triggers: Partial<Record<DisplaySettingsTriggerType, DynamicDisplayTrigger>> =
      displaySettings.triggers ?? {}
    const enabledTriggers = Object.entries(triggers).filter(([_, trigger]) => isTriggerEnabled(trigger))

    // If no triggers are enabled, show immediately (targets and frequency already passed)
    if (enabledTriggers.length === 0) {
      setShouldShow(true)
      recordDisplayOnce()
      return
    }

    const cleanupFunctions: (() => void)[] = []

    // Helper to show and record
    const showComponent = () => {
      setShouldShow(true)
      recordDisplayOnce()
    }

    // onLoad trigger
    if (isTriggerEnabled(triggers.onLoad)) {
      const delay = (triggers.onLoad.actionDelay ?? 0) * 1000
      const timer = setTimeout(() => {
        showComponent()
      }, delay)
      cleanupFunctions.push(() => clearTimeout(timer))
    }

    // onScroll trigger
    if (isTriggerEnabled(triggers.onScroll)) {
      const threshold = triggers.onScroll.threshold ?? 50
      const delay = (triggers.onScroll.actionDelay ?? 0) * 1000
      let scrollTimer: ReturnType<typeof setTimeout> | null = null

      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
        const scrollPercent = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 100

        if (scrollPercent >= threshold) {
          if (delay > 0) {
            if (!scrollTimer) {
              scrollTimer = setTimeout(() => {
                showComponent()
                window.removeEventListener('scroll', handleScroll)
              }, delay)
            }
          } else {
            showComponent()
            window.removeEventListener('scroll', handleScroll)
          }
        }
      }

      window.addEventListener('scroll', handleScroll, { passive: true })
      // Check initial scroll position
      handleScroll()

      cleanupFunctions.push(() => {
        window.removeEventListener('scroll', handleScroll)
        if (scrollTimer) clearTimeout(scrollTimer)
      })
    }

    // onClick trigger
    if (isTriggerEnabled(triggers.onClick)) {
      const clickThreshold = triggers.onClick.threshold ?? 1
      const delay = (triggers.onClick.actionDelay ?? 0) * 1000

      const handleClick = () => {
        clickCountRef.current += 1

        if (clickCountRef.current >= clickThreshold) {
          if (delay > 0) {
            setTimeout(() => {
              showComponent()
            }, delay)
          } else {
            showComponent()
          }
          document.removeEventListener('click', handleClick)
        }
      }

      document.addEventListener('click', handleClick)
      cleanupFunctions.push(() => document.removeEventListener('click', handleClick))
    }

    // onExit trigger (exit intent - mouse leaving viewport from top)
    if (isTriggerEnabled(triggers.onExit)) {
      const handleMouseLeave = (e: MouseEvent) => {
        // Only trigger when mouse exits from the top of the viewport
        if (e.clientY <= 0) {
          showComponent()
          document.removeEventListener('mouseleave', handleMouseLeave)
        }
      }

      document.addEventListener('mouseleave', handleMouseLeave)
      cleanupFunctions.push(() => document.removeEventListener('mouseleave', handleMouseLeave))
    }

    // Cleanup all event listeners on unmount or settings change
    return () => {
      for (const cleanup of cleanupFunctions) {
        cleanup()
      }
    }
  }, [demo, displaySettings, storageKey, settingsReady, recordDisplayOnce, frequencyRetry])

  return shouldShow
}
